import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { corporatesApi, formsApi, usersApi } from '../../services/api';
import {
    LayoutDashboard,
    Building2,
    MapPin,
    Users,
    Bell,
    ClipboardList,
    LogOut,
    Menu,
    X,
    ChevronDown,
    User,
    BarChart3,
    Clock,
    MessageSquare,
    HelpCircle,
    Phone,
    MapPinned,
    KeyRound,
    Eye,
    EyeOff
} from 'lucide-react';

const AdminLayout = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [corporates, setCorporates] = useState([]);
    const [selectedCorporate, setSelectedCorporate] = useState(null);
    const [corporateDropdownOpen, setCorporateDropdownOpen] = useState(false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
    const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [changePasswordError, setChangePasswordError] = useState('');
    const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifTotalCount, setNotifTotalCount] = useState(0);
    const [notifLoading, setNotifLoading] = useState(false);

    useEffect(() => {
        loadCorporates();
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [selectedCorporate]);

    const loadNotifications = async () => {
        setNotifLoading(true);
        try {
            const data = await formsApi.getSubmissions(1, 3, selectedCorporate?.id || null);
            setNotifications(data.items || []);
            setNotifTotalCount(data.totalCount || 0);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setNotifLoading(false);
        }
    };

    const formatNotifDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const loadCorporates = async () => {
        try {
            const data = await corporatesApi.getAll();
            setCorporates(data);
            // Auto-select first corporate if none selected
            if (data.length > 0 && !selectedCorporate) {
                setSelectedCorporate(data[0]);
            }
        } catch (error) {
            console.error('Error loading corporates:', error);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const handleChangePassword = async () => {
        setChangePasswordError('');
        const { currentPassword, newPassword, confirmPassword } = changePasswordForm;
        if (!currentPassword || !newPassword || !confirmPassword) {
            setChangePasswordError('Tüm alanlar zorunludur.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setChangePasswordError('Yeni şifreler eşleşmiyor.');
            return;
        }
        /*if (newPassword.length < 6) {
            setChangePasswordError('Yeni şifre en az 6 karakter olmalıdır.');
            return;
        }*/
        setChangePasswordLoading(true);
        try {
            await usersApi.changePassword(currentPassword, newPassword);
            setChangePasswordSuccess(true);
            setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setChangePasswordError(err.message || 'Şifre değiştirilemedi.');
        } finally {
            setChangePasswordLoading(false);
        }
    };

    const openChangePasswordModal = () => {
        setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setChangePasswordError('');
        setChangePasswordSuccess(false);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setUserDropdownOpen(false);
        setChangePasswordModalOpen(true);
    };

    const handleCorporateSelect = (corporate) => {
        setSelectedCorporate(corporate);
        setCorporateDropdownOpen(false);
    };

    const menuItems = [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/corporates', icon: Building2, label: 'Kurumlar' },
        { path: '/admin/locations', icon: MapPin, label: 'Lokasyonlar' },
        { path: '/admin/users', icon: Users, label: 'Kullanıcılar' },
        { path: '/admin/form-templates', icon: ClipboardList, label: 'Form Şablonları' },
        { path: '/admin/submissions', icon: Bell, label: 'Bildirimler' },
        { path: '/admin/reports', icon: BarChart3, label: 'Raporlar' },
        { path: '/admin/sms-settings', icon: MessageSquare, label: 'SMS Ayarları' },
    ];

    const getRoleName = (role) => {
        const roles = { 0: 'Süper Admin', 1: 'Yönetici', 2: 'Personel' };
        return roles[role] || 'Kullanıcı';
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-900 to-blue-800 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } lg:translate-x-0`}
            >
                <div className="flex items-center justify-between h-16 px-6 border-b border-blue-700">
                    <div className="flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-white" />
                        <span className="text-white font-bold text-lg">Yönetim</span>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-white hover:text-blue-200"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <nav className="mt-6 px-4">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${isActive
                                    ? 'bg-blue-700 text-white'
                                    : 'text-blue-100 hover:bg-blue-700/50'
                                    }`}
                            >
                                <Icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-700 space-y-1">
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-3 text-blue-100 hover:bg-blue-700/50 rounded-lg transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Çıkış Yap</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
                {/* Header */}
                <header className="sticky top-0 z-40 bg-white shadow-sm">
                    <div className="flex items-center justify-between h-16 px-4 lg:px-8">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden"
                            >
                                <Menu className="w-6 h-6" />
                            </button>

                            {/* Corporate Selector */}
                            <div className="relative">
                                <button
                                    onClick={() => setCorporateDropdownOpen(!corporateDropdownOpen)}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <Building2 className="w-4 h-4 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-700">
                                        {selectedCorporate ? selectedCorporate.name : 'Kurum Seçiniz'}
                                    </span>
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </button>

                                {corporateDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border py-2 max-h-64 overflow-y-auto">
                                        {corporates.length === 0 && (
                                            <div className="px-4 py-2 text-gray-500 text-sm">
                                                Kurum bulunamadı
                                            </div>
                                        )}
                                        {corporates.map((corporate) => (
                                            <button
                                                key={corporate.id}
                                                onClick={() => handleCorporateSelect(corporate)}
                                                className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${selectedCorporate?.id === corporate.id ? 'bg-blue-50 text-blue-700' : ''
                                                    }`}
                                            >
                                                {corporate.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right side: Notifications + User Menu */}
                        <div className="flex items-center gap-2">
                            {/* Notification Bell */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setNotifDropdownOpen(!notifDropdownOpen);
                                        setUserDropdownOpen(false);
                                    }}
                                    className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <Bell className="w-5 h-5" />
                                    {notifTotalCount > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                            {notifTotalCount > 99 ? '99+' : notifTotalCount}
                                        </span>
                                    )}
                                </button>

                                {notifDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border">
                                        <div className="px-4 py-3 border-b">
                                            <p className="text-sm font-semibold text-gray-700">Son Bildirimler</p>
                                        </div>
                                        {notifLoading ? (
                                            <div className="px-4 py-6 flex items-center justify-center">
                                                <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                            </div>
                                        ) : notifications.length === 0 ? (
                                            <div className="px-4 py-6 text-center text-gray-400 text-sm">
                                                Bildirim bulunamadı
                                            </div>
                                        ) : (
                                            <>
                                                {notifications.map((notif) => (
                                                    <div key={notif.id} className="px-4 py-3 hover:bg-gray-50 border-b last:border-b-0">
                                                        <p className="text-sm font-medium text-gray-800 truncate">{notif.formTitle}</p>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                            <span className="text-xs text-gray-500 truncate">{notif.locationName}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                            <span className="text-xs text-gray-400">{formatNotifDate(notif.submittedAt)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="px-4 py-2 border-t">
                                                    <button
                                                        onClick={() => { navigate('/admin/submissions'); setNotifDropdownOpen(false); }}
                                                        className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-1 hover:bg-blue-50 rounded-lg transition-colors"
                                                    >
                                                        Tüm Bildirimleri Görüntüle
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* User Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setUserDropdownOpen(!userDropdownOpen);
                                        setNotifDropdownOpen(false);
                                    }}
                                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="hidden md:block text-left">
                                        <p className="text-sm font-medium text-gray-700">{user?.fullName}</p>
                                        <p className="text-xs text-gray-500">{getRoleName(user?.roleId)}</p>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-gray-500 hidden md:block" />
                                </button>

                                {userDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border py-2">
                                        <div className="px-4 py-2 border-b">
                                            <p className="text-sm font-medium text-gray-700">{user?.fullName}</p>
                                            <p className="text-xs text-gray-500">{user?.email}</p>
                                        </div>
                                        <button
                                            onClick={() => { setUserDropdownOpen(false); setHelpModalOpen(true); }}
                                            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <HelpCircle className="w-4 h-4 text-blue-500" />
                                            Yardım
                                        </button>
                                        <button
                                            onClick={openChangePasswordModal}
                                            className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <KeyRound className="w-4 h-4 text-blue-500" />
                                            Şifremi Değiştir
                                        </button>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            Çıkış Yap
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-4 lg:p-8">
                    <Outlet context={{ selectedCorporate, corporates }} />
                </main>
                {/* Footer */}
                <footer className="text-center py-4 text-xs text-gray-400 border-t border-gray-200">
                    © 2026 BOBO Yazılım · Tüm Hakları Saklıdır.
                </footer>
            </div>

            {/* Help Modal */}
            {helpModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <div className="flex items-center gap-2">
                                <HelpCircle className="w-5 h-5 text-blue-600" />
                                <h3 className="text-lg font-semibold text-gray-900">Kurum Bilgileri</h3>
                            </div>
                            <button
                                onClick={() => setHelpModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {selectedCorporate ? (
                                <>
                                    <div className="flex items-start gap-3">
                                        <Building2 className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Kurum Adı</p>
                                            <p className="text-gray-800 font-medium">{selectedCorporate.name}</p>
                                        </div>
                                    </div>
                                    {selectedCorporate.phoneNumber && (
                                        <div className="flex items-start gap-3">
                                            <Phone className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Telefon</p>
                                                <p className="text-gray-800">{selectedCorporate.phoneNumber}</p>
                                            </div>
                                        </div>
                                    )}
                                    {selectedCorporate.address && (
                                        <div className="flex items-start gap-3">
                                            <MapPinned className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Adres</p>
                                                <p className="text-gray-800">{selectedCorporate.address}</p>
                                            </div>
                                        </div>
                                    )}
                                    {!selectedCorporate.phoneNumber && !selectedCorporate.address && (
                                        <p className="text-sm text-gray-500 text-center py-2">Ek bilgi bulunamadı.</p>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-2">Lütfen önce bir kurum seçin.</p>
                            )}
                        </div>
                        <div className="px-6 pb-5">
                            <button
                                onClick={() => setHelpModalOpen(false)}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Modal */}
            {changePasswordModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <div className="flex items-center gap-2">
                                <KeyRound className="w-5 h-5 text-blue-500" />
                                <h3 className="text-lg font-semibold text-gray-900">Şifremi Değiştir</h3>
                            </div>
                            <button
                                onClick={() => setChangePasswordModalOpen(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {changePasswordSuccess ? (
                                <div className="text-center py-4 space-y-3">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                        <KeyRound className="w-6 h-6 text-green-600" />
                                    </div>
                                    <p className="text-green-700 font-medium">Şifreniz başarıyla değiştirildi.</p>
                                </div>
                            ) : (
                                <>
                                    {changePasswordError && (
                                        <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg">
                                            {changePasswordError}
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">MEVCUT ŞİFRE</label>
                                        <div className="relative">
                                            <input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={changePasswordForm.currentPassword}
                                                onChange={(e) => setChangePasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Mevcut şifrenizi girin"
                                            />
                                            <button type="button" onClick={() => setShowCurrentPassword(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">YENİ ŞİFRE</label>
                                        <div className="relative">
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={changePasswordForm.newPassword}
                                                onChange={(e) => setChangePasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Yeni şifrenizi girin"
                                            />
                                            <button type="button" onClick={() => setShowNewPassword(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">YENİ ŞİFRE TEKRAR</label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={changePasswordForm.confirmPassword}
                                                onChange={(e) => setChangePasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Yeni şifrenizi tekrar girin"
                                            />
                                            <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="px-6 pb-5 flex gap-2">
                            {changePasswordSuccess ? (
                                <button
                                    onClick={() => setChangePasswordModalOpen(false)}
                                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Kapat
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setChangePasswordModalOpen(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        İptal
                                    </button>
                                    <button
                                        onClick={handleChangePassword}
                                        disabled={changePasswordLoading}
                                        className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                                    >
                                        {changePasswordLoading && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>}
                                        Değiştir
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
};

export default AdminLayout;
